import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import CommonModal from "../components/CommonModal";

const Bank_guarantee = () => {
  const [licenseOptions, setLicenseOptions] = useState([]);
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  // ------------------------------
  // Dynamic Form Field Config
  // ------------------------------
  const formFields = useMemo(() => [
    { name: "license_id_fk", label: "license No", type: "select", required: true, col: 6,
      options: licenseOptions 
    },
    { name: "consignee_id_fk", label: "consignee", type: "select", col: 6,
      options: consigneeOptions
    },
    { name: "bg_number", label: "BG Number", type: "text", col: 6 },
    { name: "bank_name", label: "Bank Name", type: "text", col: 6 },
    { name: "bg_amount", label: "BG Amount", type: "text", col: 6 },
    { name: "bg_issue_date", label: "BG Issue Date", type: "date", col: 6 },
    { name: "bg_expiry_date", label: "BG Expiry Date", type: "date", col: 6 },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: 0, label: "Active" },
        { value: 1, label: "Inactive" },
      ],
      col: 6,
    },
    { name: "remarks", label: "Remarks", type: "textarea", col: 12 },
  ], [licenseOptions,consigneeOptions]);

  // ------------------------------
  // Initial Form State
  // ------------------------------
  const initialFormState = useMemo(() => {
    return formFields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, { bank_guarantee_id: 0 });
  }, [formFields]);

  const [formData, setFormData] = useState(initialFormState);
  const [bank_guaranteeList, setBank_guaranteeList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------

  const fetchLicenseData = useCallback(async () => {
      try {
        const response = await post("/compliance/license/list");
        if (response.status === 200) {
          setLicenseOptions([
              { value: "", label: "Select License" },
              ...response.data.map(item => ({
                value: item.license_id,
                label: item.license_no,
              }))
            ]);
        }
      } catch (err) {
        console.log(err.message);
      }
    }, []);
    const fetchConsigneeData = useCallback(async () => {
      try {
        const response = await post("/masters/consignees/list");
        if (response.status === 200) {
           setConsigneeOptions([
              { value: "", label: "Select Consignee" },
              ...response.data.map(item => ({
                value: item.consignees_id,
                label: item.consignees_name,
              }))
            ]);
        }
      } catch (err) {
        console.log(err.message);
      }
    }, []);
  const fetchData = useCallback(async () => {
    try {
      const response = await post("/compliance/bank_guarantee/list");
      if (response.status === 200) {
        setBank_guaranteeList(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchData();
    fetchLicenseData();
    fetchConsigneeData();
  }, [fetchData,fetchLicenseData,fetchConsigneeData]);

  // ------------------------------
  // Edit Handler (FIXED POSITION)
  // ------------------------------
  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });
    setShowModal(true);
  }, [initialFormState]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
      {
        header: "Actions",
        cell: ({ row }) => (
          <i
            className="ri-file-edit-fill text-primary cursor-pointer me-2"
            onClick={() => handleEdit(row)}
            title="Edit"
            style={{ fontSize: "18px" }}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      { header: "License No", accessorKey: "license_no" },
      { header: "Consignee", accessorKey: "consignees_name" },
      { header: "BG Number", accessorKey: "bg_number" },
      { header: "Bank Name", accessorKey: "bank_name" },
      { header: "BG Amount", accessorKey: "bg_amount" },
      { header: "BG Issue Date", accessorKey: "bg_issue_date" },
      { header: "BG Expiry Date", accessorKey: "bg_expiry_date" },
      { header: "Remarks", accessorKey: "remarks" },
      { header: "Status", accessorKey: "status", label_condition: "status" },
    ],
    [handleEdit]
  );

  // ------------------------------
  // Input Change Handler
  // ------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ------------------------------
  // Close Modal
  // ------------------------------
  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
  };

  // ------------------------------
  // Submit Form
  // ------------------------------
  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    try {
      const response = await post("/compliance/bank_guarantee", formData);
      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        fetchData();
        handleClose();
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Bank Guarantee</div>
          <div className="page-header-sub">BG master linked to licenses</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          Add Bank Guarantee
        </button>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Bank Guarantee List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Bank_guarantee List"
            columns={columns}
            data={bank_guaranteeList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      <CommonModal
        show={showModal}
        title="Bank guarantee List"
        formFields={formFields}
        formData={formData}
        onChange={handleChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
      />
    </>
  );
};

export default Bank_guarantee;